import axios from "axios";
import * as Sentry from "@sentry/react";
import { getStoredAccessToken } from "@/utils/authStorage";

type RequestConfigWithAuthFlag = {
  withoutAuth?: boolean;
  headers?: Record<string, unknown>;
};

const api = axios.create({
  baseURL: (import.meta.env.VITE_REACT_API_URL as string).replace(/\/$/, ""),
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const config = error.config as RequestConfigWithAuthFlag | undefined;
    const withoutAuth =
      config?.withoutAuth === true ||
      config?.headers?.withoutAuth === true ||
      config?.headers?.withoutAuth === "true";

    // Sessão expirada: limpar storage e redirecionar para login
    if (status === 401 && !withoutAuth) {
      localStorage.removeItem("token");
      localStorage.removeItem("empresaId");
      localStorage.removeItem("dados");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // Erros de rede/conectividade (sem resposta HTTP) não são bugs — não enviar ao Sentry
    const isNetworkError = !error.response;
    const isAborted =
      error.code === "ECONNABORTED" || error.message === "Request aborted";

    if (import.meta.env.PROD && !isNetworkError && !isAborted) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status,
        },
      });
    } else if (!import.meta.env.PROD) {
      console.error("[API Error]", error);
    }
    return Promise.reject(error);
  },
);

api.interceptors.request.use(
  (config) => {
    if (config?.headers?.withoutAuth === true) {
      (config as RequestConfigWithAuthFlag).withoutAuth = true;
      delete config.headers.withoutAuth;
      return config;
    }
    const empresaId = window.localStorage.getItem("empresaId");
    if (empresaId) {
      config.headers["x-empresa-id"] = empresaId;
    }
    const token = getStoredAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export default api;
